import { useEffect, useState } from "react";
import CategorySection from "../components/Category";
import FullLibrarySection from "../components/Home/FullLibrary";
import Hero from "../components/Home/HeroSection";
import MarqueeStrip from "../components/Home/MarqueeStrip";
import NewArrivalsSection from "../components/Home/NewArrival";
import AuthorSection from "../components/AuthorSection";
import DynamicBookSlider from "../components/Home/DynamicBookSlider";
import { fetchHomepageSections, fetchBanners, fetchAuthors } from "../api";
import { buildManualSectionBooks, normalizeHomepageSections, SPECIAL_SECTION_KEYS } from "../homepageSections";

// Fallback banners used when no banners are configured in the admin panel
const FALLBACK_BANNERS = [
	{ id: 'promo-1', image: "/banner/1.jpeg", href: "/store" },
	{ id: 'promo-2', image: "/banner/2.jpeg", href: "/store" },
	{ id: 'promo-3', image: "/banner/3.jpeg", href: "/store" },
	{ id: 'promo-4', image: "/banner/4.jpeg", href: "/store" },
	{ id: 'promo-5', image: "/banner/5.jpeg", href: "/store" },
	{ id: 'promo-6', image: "/banner/6.jpeg", href: "/store" },
	{ id: 'promo-7', image: "/banner/7.jpeg", href: "/store" },
];

export default function HomePage({ books, onView, cart, addCart }) {
	const [sectionConfigs, setSectionConfigs] = useState({});
	const [homeBanners, setHomeBanners] = useState(FALLBACK_BANNERS);
	const [authors, setAuthors] = useState([]);

	useEffect(() => {
		let isMounted = true;

		const loadHomepageSections = async () => {
			try {
				const data = await fetchHomepageSections();
				if (isMounted) {
					setSectionConfigs(normalizeHomepageSections(data.sections));
				}
			} catch (error) {
				console.error("Failed to load homepage section config:", error);
			}
		};

		const loadBanners = async () => {
			try {
				const data = await fetchBanners();
				if (isMounted && Array.isArray(data) && data.length > 0) {
					setHomeBanners(data.map((b) => ({
						id: `banner-${b.id}`,
						image: b.image_url,
						href: b.href || "/store",
					})));
				}
			} catch (error) {
				console.error("Failed to load banners, using fallbacks:", error);
			}
		};

		const loadAuthors = async () => {
			try {
				const data = await fetchAuthors();
				if (isMounted && Array.isArray(data)) {
					setAuthors(data);
				}
			} catch (error) {
				console.error("Failed to load authors:", error);
			}
		};

		loadHomepageSections();
		loadBanners();
		loadAuthors();

		return () => {
			isMounted = false;
		};
	}, []);

	// Sort sections by sortOrder
	const sortedSections = Object.values(sectionConfigs)
		.sort((a, b) => a.sortOrder - b.sortOrder);

	return (
		<div>
			<Hero banners={homeBanners} />
			<MarqueeStrip />
			<NewArrivalsSection books={books} onView={onView} cart={cart} addCart={addCart} />

			{/* Dynamic Admin-Managed Sections */}
			{sortedSections.map(section => {
				if (!section.isActive) return null;

				if (section.sectionKey === "category") {
					return (
						<CategorySection
							key={section.sectionKey}
							books={books}
							selectedBooks={buildManualSectionBooks(books, section)}
							itemLimit={section.itemLimit}
							onView={onView}
							cart={cart}
							addCart={addCart}
						/>
					);
				}

				if (section.sectionKey === "authors") {
					return (
						<AuthorSection key={section.sectionKey} authors={authors} />
					);
				}

				// All other dynamic sections are standard book sliders
				return (
					<DynamicBookSlider
						key={section.sectionKey}
						title={section.label || "Books"}
						books={books}
						selectedBooks={buildManualSectionBooks(books, section)}
						itemLimit={section.itemLimit}
						onView={onView}
						cart={cart}
						addCart={addCart}
					/>
				);
			})}

			<FullLibrarySection books={books} onView={onView} cart={cart} addCart={addCart} />

		</div>
	);
}
